<template>
 <!-- Summary Cards Row -->
 <v-row dense>
      <v-col cols="12" sm="6" md="3">
        <v-skeleton-loader
          v-if="!dataLoaded"
          type="card"
          height="150"
        ></v-skeleton-loader>
        <TotalCardWithSelect
          v-else
          :items="[$t('totalItems'), $t('totalOpenItems')]"
          :count="totalItems" 
          :tooltip="showOpenItemsOnly ? $t('totalOpenItemsToolTip') : ''"          
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-skeleton-loader
          v-if="!dataLoaded"
          type="card"
          height="150"
        ></v-skeleton-loader>
        <TotalCard
          v-else
          identifier="totalSubItemsCard"
          :label="$t('totalSubItems')"
          :count="totalSubItems"
          :tooltip="$t('totalSubItemsTooltip')"        
        />  
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-skeleton-loader
          v-if="!dataLoaded"
          type="card"
          height="150"
        ></v-skeleton-loader>
        <TotalCardWithSwitch
          v-else
          identifier="totalAssignedToYourTeams"
          :label="$t('assignedToYourTeams')"
          :count="totalAssignedToYourTeams"
          @filter-change="handleFilterChange"
          v-model:selected="filterByAssignedToMyTeams"   
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-skeleton-loader
          v-if="!dataLoaded"
          type="card"
          height="150"
        ></v-skeleton-loader>
        <TotalCardWithSwitch
          v-else
          identifier="totalAssignedToYou"
          :label="$t('assignedToYou')"
          :count="totalAssignedToYou"
          @filter-change="handleFilterChange"
          v-model:selected="filterByAssignedToMe"   
        />
      </v-col>
    </v-row>
</template>



<script setup>
import { computed, watch, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { debounce } from 'lodash';
import TotalCard from '../cards/TotalCard.vue';
import TotalCardWithSelect from '../cards/TotalCardWithSelect.vue';
import TotalCardWithSwitch from '../cards/TotalCardWithSwitch.vue';

const store = useStore();
const settings = computed(() => store.state.settings);

const dataLoaded = computed(() => store.getters.getDataLoaded);
const conn = store.getters.getDBConnection;

const userId = computed(() => store.getters.getUserId);

const filteredItems = computed(() => store.getters.getFilteredItems);
const filterContext = computed(() => store.state.filterItemsContext);

const showOpenItemsOnly = computed(() => store.getters.getShowOpenItemsOnly);
const userTeamsObj = computed(() => store.getters.getUserTeamsObj);


const totalItems = ref(0);
const totalSubItems = ref(0);
const totalAssignedToYourTeams = ref(0);
const totalAssignedToYou = ref(0);


const computeCounts = async () => {
  if (isMounted.value && dataLoaded.value) {
    // Combined query
    const combinedQuery = `
      SELECT 
        COUNT(DISTINCT id) AS TotalItems,
        COUNT(id) AS TotalSubItems,
        COUNT(CASE WHEN AssigneeTeamId IN (
            SELECT team_users.id
            FROM team_users
            WHERE json_extract_string(users, '$[0].id') = '${userId.value}'
        ) THEN id END) AS AssignedToMyTeams,
        COUNT(CASE WHEN array_contains(AssigneeUserIds, '${userId.value}') THEN id END) AS AssignedToMe
      FROM filteredItems
    `;

    try {
      const result = await conn.query(combinedQuery);
      const row = result.toArray()[0];
      
      if (!row) {
        totalItems.value = 0;
        totalSubItems.value = 0;
        totalAssignedToYourTeams.value = 0;
        totalAssignedToYou.value = 0;
        return;
      }

      const jsonRow = row.toJSON();
      
      totalItems.value = Number(jsonRow.TotalItems);
      totalSubItems.value = Number(jsonRow.TotalSubItems);
      totalAssignedToYourTeams.value = Number(jsonRow.AssignedToMyTeams);
      totalAssignedToYou.value = Number(jsonRow.AssignedToMe);
      
    } catch (error) {
      console.error("Error executing combined query:", error);
      totalItems.value = 0;
      totalSubItems.value = 0;
      totalAssignedToYourTeams.value = 0;
      totalAssignedToYou.value = 0;
    }
  }
};

const handleFilterChange = ({ id, selected }) => {
    if (id === 'totalAssignedToYourTeams') {
      filterByAssignedToMyTeams(selected);
    } else if (id === 'totalAssignedToYou') {
      filterByAssignedToMe(selected);
    }
  };

  const filterByAssignedToMyTeams = (selected) => {
    const assignedToYourTeamFilter = {};
    if (selected) {
      filteredItems.value.forEach(item => {
        item.subItems.forEach(subItem => {
          if (subItem.assigneeTeamId && subItem.assigneeTeamId in userTeamsObj.value) {
            assignedToYourTeamFilter[subItem.id] = true;
          }
        });
      });
    }
    store.commit('updateFilter', {chartId:'assignedToYourTeamsCardFilterContext', ids:assignedToYourTeamFilter});
  };

  const filterByAssignedToMe = (selected) => {
    const assignedToMeFilter = {};
    if (selected) {
      filteredItems.value.forEach(item => {
        item.subItems.forEach(subItem => {
          if (subItem.assigneeUserIds && subItem.assigneeUserIds.includes(settings.value.userId)) {
            assignedToMeFilter[subItem.id] = true;
          }
        });
      });
    }
    store.commit('updateFilter', {chartId:'assignedToMeCardFilterContext', ids:assignedToMeFilter});
  };



  const recomputeCounts = debounce(() => {
    computeCounts();
  }, 100);

  watch(
      () => filterContext.value.filtersLastUpdated,
      () => {
        if (isMounted.value) {
          recomputeCounts();
        }
      },
      { deep: true }
    );

const isMounted = ref(false);
onMounted(async () => {
  //await store.commit('resetFilters');
  recomputeCounts(filteredItems); 
  isMounted.value = true;
});


</script>
